import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useGetOrgData } from "~hooks/useGetOrgData";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useExtractCompetitionsFromScope } from "~pages/Competitions/CompetitionsListPage/hooks/useExtractCompetitionsFromScope.ts";
import type { CompetitionTransformed } from "~pages/Competitions/types/competitionTransformed.ts";
import useDeleteCompetition from "../hooks/useDeleteCompetition.ts";
import { competitionsExportFields, defaultCompetitionsExportFieldNames } from "./constants/exportFields.ts";
import { useExportCompetitions } from "./hooks/useExportCompetitions.ts";
import { useFilteredCompetitions } from "./hooks/useFilteredCompetitions.ts";
import { useGetCompetitionsTableColumns } from "./hooks/useGetCompetitionsTableColumns.tsx";
import { useGetCompetitionsTableFilters } from "./hooks/useGetCompetitionsTableFilters.tsx";
import { useImportCompetitions } from "./hooks/useImportCompetitions.ts";

const CompetitionsList = () => {
  const { sport, organizationId, customerId } = useGetOrgData();
  const location = useLocation();
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteCompetition();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(50, 1, [50, 100, 500, 1000]);
  const { formatMessage } = useIntl();

  const { mutateAsync: startImporting } = useImportCompetitions();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportCompetitions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableImportConfiguration = uiConfiguration.pages.competitions.listPage.importConfig;
  const dataTableUiConfiguration = uiConfiguration.pages.competitions.listPage;

  const { competitions, isLoading, refetch } = useExtractCompetitionsFromScope();

  const handleDelete = useCallback(
    async (competitionId: string) => {
      return await deleteFn(competitionId);
    },
    [deleteFn],
  );

  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/manage/`);

  const columns = useGetCompetitionsTableColumns();
  const filtersConfig = useGetCompetitionsTableFilters();

  const clientCompetitions = useFilteredCompetitions({
    competitions,
    currentFilter: filtersConfig.currentFilter,
    searchText,
  });

  return (
    <div>
      <DataTable
        dataTableUiConfiguration={dataTableUiConfiguration}
        title={formatMessage({ id: "competitionsListPage.title" })}
        handleRefresh={() => {
          refetch();
        }}
        columns={columns}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        rowActions={{
          base: {
            manage: {
              onClick: (row: CompetitionTransformed) =>
                navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/manage/${row.competitionId}`),
            },
            delete: {
              onClick: (row: CompetitionTransformed) => handleDelete(row.competitionId),
              loading: isDeletePending,
            },
          },
        }}
        handleAdd={handleAdd}
        addButtonIntlKey={"competition"}
        isDataLoading={isLoading}
        filtersConfig={filtersConfig}
        rowKey={"competitionId"}
        rowNameKey={"nameLocal"}
        paginationConfig={{ ...paginationConfig, total: clientCompetitions.length }}
        dataSource={clientCompetitions}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultCompetitionsExportFieldNames,
          groupedFields: competitionsExportFields,
          localStorageKey: "export.competitions",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.competitions`}
        columnSortConfig={{
          sorterLocalStorageKey: "tables.settings.sort.competitions",
          defaultSorter: {
            columnKey: "nameLocal",
            order: "ascend",
          },
        }}
        permissionNames={[PermissionNames.COMPETITION]}
      />
    </div>
  );
};

export default CompetitionsList;
